import { useMutation, useQuery, UseQueryResult } from 'react-query'
import {
  makeCoreGetRequest,
  makeCorePatchRequest,
  makeCoreDeleteRequest,
  makeCorePostRequest
} from 'utils/api'
import { handleQueryError } from 'utils/helpers'
import {
  AssignmentTableItem,
  ManagersApiResponse,
  ManagersTableItem,
  PartnersApiResponse,
  PartnersTableItem,
  AssignmentStatusApiResponse,
  AssignmentExternalPartnerTypeApiResponse,
  DelegateTableItem,
  DelegatesApiResponse,
  DeleteDelegateArgs,
  StoreSummary
} from './interfaces'

export const usePartnersQuery = (
  showNotification: Function,
  getParams: string
): UseQueryResult<PartnersApiResponse, Error> => {
  return useQuery<PartnersApiResponse, Error>(
    ['partners', getParams],
    async () => {
      const result = await makeCoreGetRequest(getParams)
      return result
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error fetching partners.', showNotification),
      staleTime: 200
    }
  )
}

export const useManagersQuery = (
  showNotification: Function,
  getParams: string,
  options: { enabled: boolean } = { enabled: true }
): UseQueryResult<any, Error> => {
  return useQuery<any, Error>(
    ['managers', getParams],
    async () => {
      const result = await makeCoreGetRequest(getParams)
      return result
    },
    {
      ...options,
      onError: error =>
        handleQueryError(error, 'There was an error fetching managers.', showNotification),
      staleTime: 200
    }
  )
}

export const useDelegatesQuery = (
  showNotification: Function,
  getParams: string,
  options: { enabled: boolean } = { enabled: true }
): UseQueryResult<any, Error> => {
  return useQuery<any, Error>(
    ['delegates', getParams],
    async () => {
      const result = await makeCoreGetRequest(getParams)
      return result
    },
    {
      ...options,
      onError: error =>
        handleQueryError(error, 'There was an error fetching delegates.', showNotification),
      staleTime: 200
    }
  )
}

export const useStoresQuery = (
  showNotification: Function,
  getParams: string,
  options: { enabled: boolean } = { enabled: true }
): UseQueryResult<StoreSummary[], Error> => {
  return useQuery<StoreSummary[], Error>(
    ['stores', getParams],
    async () => {
      const result = await makeCoreGetRequest(getParams)
      return result
    },
    {
      ...options,
      onError: error =>
        handleQueryError(error, 'There was an error fetching stores.', showNotification),
      staleTime: 200
    }
  )
}

export const useCountriesQuery = (
  showNotification: Function,
  getParams: string,
  options: { enabled: boolean } = { enabled: true }
): UseQueryResult<any, Error> => {
  // TODO define a proper type for countries
  // This is a placeholder, replace with actual type when available
  return useQuery<any, Error>(
    ['countries', getParams],
    async () => {
      const result = await makeCoreGetRequest(getParams)
      return result
    },
    {
      ...options,
      onError: error =>
        handleQueryError(error, 'There was an error fetching countries.', showNotification),
      staleTime: 200
    }
  )
}

export const useLocationTypesQuery = (
  showNotification: Function,
  getParams: string,
  options: { enabled: boolean } = { enabled: true }
): UseQueryResult<any, Error> => {
  // TODO define a proper type for location types
  // This is a placeholder, replace with actual type when available
  return useQuery<any, Error>(
    ['location-types', getParams],
    async () => {
      const result = await makeCoreGetRequest(getParams)
      return result
    },
    {
      ...options,
      onError: error =>
        handleQueryError(error, 'There was an error fetching location types.', showNotification),
      staleTime: 200
    }
  )
}

export const useLegalEntityTypesQuery = (
  showNotification: Function,
  getParams: string,
  options: { enabled: boolean } = { enabled: true }
): UseQueryResult<any, Error> => {
  // TODO define a proper type for legal entity types
  // This is a placeholder, replace with actual type when available
  return useQuery<any, Error>(
    ['legal-entity-types', getParams],
    async () => {
      const result = await makeCoreGetRequest(getParams)
      return result
    },
    {
      ...options,
      onError: error =>
        handleQueryError(
          error,
          'There was an error fetching legal entity types.',
          showNotification
        ),
      staleTime: 200
    }
  )
}

export const useStatusAssignmentQuery = (
  showNotification: Function,
  getParams: string,
  options: { enabled: boolean } = { enabled: true }
): UseQueryResult<AssignmentStatusApiResponse, Error> => {
  return useQuery<AssignmentStatusApiResponse, Error>(
    ['status-assignment', getParams],
    async () => {
      const result = await makeCoreGetRequest(getParams)
      return result
    },
    {
      ...options,
      onError: error =>
        handleQueryError(
          error,
          'There was an error fetching the status assignment.',
          showNotification
        ),
      staleTime: 200
    }
  )
}

export const useLegalEntityTypeQuery = (
  showNotification: Function,
  externalPertnerTypeGroup: string,
  options: { enabled: boolean } = { enabled: true }
): UseQueryResult<any, Error> => {
  return useQuery<any, Error>(
    ['legal-entity-type', externalPertnerTypeGroup],
    async () => {
      const result = await makeCoreGetRequest(
        `/iam/legal-entity-type/name?group=${externalPertnerTypeGroup}`
      )
      return result
    },
    {
      ...options,
      onError: error =>
        handleQueryError(
          error,
          'There was an error fetching the legal entity type.',
          showNotification
        ),
      staleTime: 200
    }
  )
}

export const useExternalTypesQuery = (
  showNotification: Function,
  getParams: string,
  options: { enabled: boolean } = { enabled: true }
): UseQueryResult<AssignmentExternalPartnerTypeApiResponse, Error> => {
  return useQuery<AssignmentExternalPartnerTypeApiResponse, Error>(
    ['external-types', getParams],
    async () => {
      const result = await makeCoreGetRequest(getParams)
      return result
    },
    {
      ...options,
      onError: error =>
        handleQueryError(
          error,
          'There was an error fetching external types for assignment.',
          showNotification
        ),
      staleTime: 200
    }
  )
}

export const useLocationsQuery = (
  showNotification: Function,
  getParams: string,
  options: { enabled: boolean } = { enabled: true }
): UseQueryResult<any, Error> => {
  return useQuery<any, Error>(
    ['iam-locations', getParams],
    async () => {
      const result = await makeCoreGetRequest(getParams)
      return result
    },
    {
      ...options,
      onError: error =>
        handleQueryError(error, 'There was an error fetching locations.', showNotification),
      staleTime: 200
    }
  )
}

export const useLegalEntitiesQuery = (
  showNotification: Function,
  getParams: string,
  options: { enabled: boolean } = { enabled: true }
): UseQueryResult<any, Error> => {
  return useQuery<any, Error>(
    ['iam-legal-entities', getParams],
    async () => {
      const result = await makeCoreGetRequest(getParams)
      return result
    },
    {
      ...options,
      onError: error =>
        handleQueryError(error, 'There was an error fetching legal entities.', showNotification),
      staleTime: 200
    }
  )
}

export const usePartnerQuery = (
  showNotification: Function,
  partnerId: number,
  shouldRefreshData: boolean,
  options: { enabled?: boolean } = {}
): UseQueryResult<PartnersTableItem, Error> => {
  return useQuery<PartnersTableItem, Error>(
    ['partner', partnerId, shouldRefreshData],
    async () => {
      const result = await makeCoreGetRequest(`/iam/external-partners/people/${partnerId}`)
      return result
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error fetching the partner.', showNotification),
      enabled: !!partnerId && (options.enabled ?? true),
      staleTime: 200
    }
  )
}

export const useManagerQuery = (
  showNotification: Function,
  managerId: number,
  shouldRefreshData: boolean
): UseQueryResult<ManagersTableItem, Error> => {
  return useQuery<ManagersTableItem, Error>(
    ['manager', managerId, shouldRefreshData],
    async () => {
      const result = await makeCoreGetRequest(`/iam/external-partners/managers/${managerId}`)
      return result
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error fetching the partner.', showNotification),
      enabled: !!managerId,
      staleTime: 200
    }
  )
}

export const useDelegateQuery = (
  showNotification: Function,
  delegateId: number,
  shouldRefreshData: boolean
): UseQueryResult<DelegateTableItem, Error> => {
  return useQuery<DelegateTableItem, Error>(
    ['delegate', delegateId, shouldRefreshData],
    async () => {
      const result = await makeCoreGetRequest(`/iam/external-partners/delegates/${delegateId}`)
      return result
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error fetching the delegate.', showNotification),
      enabled: !!delegateId,
      staleTime: 200
    }
  )
}

export const useAssignmentQuery = (
  showNotification: Function,
  assignmentId: number | null,
  shouldRefreshData: boolean,
  enabled: boolean = true
): UseQueryResult<AssignmentTableItem, Error> => {
  return useQuery<AssignmentTableItem, Error>(
    ['assignment', assignmentId, shouldRefreshData],
    async () => {
      const result = await makeCoreGetRequest(`/iam/external-partners/assignments/${assignmentId}`)
      return result
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error fetching the assignment.', showNotification),
      enabled: !!assignmentId && enabled,
      staleTime: 500,
      cacheTime: 500
    }
  )
}

export const useLegalEntityQuery = (
  showNotification: Function,
  legalEntityId: number | null,
  shouldRefreshData: boolean,
  enabled: boolean = true
): UseQueryResult<any, Error> => {
  return useQuery<any, Error>(
    ['legalEntity', legalEntityId, shouldRefreshData],
    async () => {
      const result = await makeCoreGetRequest(
        `/iam/external-partners/legal-entities/${legalEntityId}`
      )
      return result
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error fetching the legal entity.', showNotification),
      enabled: !!legalEntityId && enabled,
      staleTime: 500,
      cacheTime: 500
    }
  )
}

export const useStoreQuery = (
  showNotification: Function,
  storeId: number | null,
  shouldRefreshData: boolean,
  enabled: boolean = true
): UseQueryResult<any, Error> => {
  return useQuery<any, Error>(
    ['store', storeId, shouldRefreshData],
    async () => {
      const result = await makeCoreGetRequest(`/iam/external-partners/stores/${storeId}`)
      return result
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error fetching the store.', showNotification),
      enabled: !!storeId && enabled,
      staleTime: 500,
      cacheTime: 500
    }
  )
}

export const useCreatePartnerMutation = (showNotification: Function) => {
  return useMutation<PartnersTableItem, Error, Partial<PartnersTableItem>>(
    async newPartnerData => {
      const result = await makeCorePostRequest('/iam/external-partners/people', newPartnerData)
      return result
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error creating the partner.', showNotification),
      onSuccess: () =>
        showNotification({ message: 'Partner created successfully', type: 'success' })
    }
  )
}

export const useCreateManagerMutation = (showNotification: Function) => {
  return useMutation<ManagersTableItem, Error, Partial<ManagersTableItem>>(
    async newManagerData => {
      const result = await makeCorePostRequest('/iam/external-partners/managers', newManagerData)
      return result
    },
    {
      onError: error => handleQueryError(error, '', showNotification),
      onSuccess: () =>
        showNotification({ message: 'Manager created successfully', type: 'success' })
    }
  )
}

export const useCreateDelegateMutation = (showNotification: Function) => {
  return useMutation<DelegateTableItem, Error, Partial<DelegateTableItem>>(
    async newDelegateData => {
      const result = await makeCorePostRequest('/iam/external-partners/delegates', newDelegateData)
      return result
    },
    {
      onError: error => handleQueryError(error, '', showNotification),
      onSuccess: () =>
        showNotification({ message: 'Delegate created successfully', type: 'success' })
    }
  )
}

export const useCreateAssignmentMutation = (showNotification: Function) => {
  return useMutation<AssignmentTableItem, Error, Partial<AssignmentTableItem>>(
    async newAssignmentData => {
      const result = await makeCorePostRequest(
        '/iam/external-partners/assignments',
        newAssignmentData
      )
      return result
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error creating the assignment.', showNotification),
      onSuccess: () =>
        showNotification({ message: 'Assignment created successfully', type: 'success' })
    }
  )
}

export const useUpdatePartnerMutation = (showNotification: Function) => {
  return useMutation<
    PartnersTableItem,
    Error,
    { partnerId: number; updatedData: Partial<PartnersTableItem> }
  >(
    async ({ partnerId, updatedData }) => {
      const result = await makeCorePatchRequest(
        `/iam/external-partners/people/${partnerId}`,
        updatedData
      )
      return result
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error updating the partner.', showNotification),
      onSuccess: () =>
        showNotification({ message: 'Partner updated successfully', type: 'success' })
    }
  )
}

export const useUpdateManagerMutation = (showNotification: Function, onError = () => {}) => {
  return useMutation<
    ManagersTableItem,
    Error,
    { managerId: number; updatedData: Partial<ManagersTableItem> }
  >(
    async ({ managerId, updatedData }) => {
      const result = await makeCorePatchRequest(
        `/iam/external-partners/managers/${managerId}`,
        updatedData
      )
      return result
    },
    {
      onError: error => {
        onError && onError()
        handleQueryError(error, '', showNotification)
      },
      onSuccess: () =>
        showNotification({ message: 'Manager updated successfully', type: 'success' })
    }
  )
}

export const useUpdateDelegateMutation = (showNotification: Function, onError = () => {}) => {
  return useMutation<
    DelegateTableItem,
    Error,
    { managerId: number; delegateId: number; updatedData: Partial<DelegateTableItem> }
  >(
    async ({ managerId, delegateId, updatedData }) => {
      const result = await makeCorePostRequest(
        `/iam/external-partners/managers/${managerId}/delegate/${delegateId}`,
        updatedData
      )
      return result
    },
    {
      onError: error => {
        onError && onError()
        handleQueryError(error, '', showNotification)
      },
      onSuccess: () =>
        showNotification({ message: 'Delegate updated successfully', type: 'success' })
    }
  )
}

export const useUpdateAssignmentMutation = (showNotification: Function) => {
  return useMutation<
    AssignmentTableItem,
    Error,
    { assignmentId: number; updatedData: Partial<AssignmentTableItem> }
  >(
    async ({ assignmentId, updatedData }) => {
      const result = await makeCorePatchRequest(
        `/iam/external-partners/assignments/${assignmentId}`,
        updatedData
      )
      return result
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error updating the assignment.', showNotification),
      onSuccess: () =>
        showNotification({ message: 'Assignment updated successfully', type: 'success' })
    }
  )
}

export const useDeletePartnerMutation = (showNotification: Function) => {
  return useMutation<void, Error, string>(
    async partnerId => {
      await makeCoreDeleteRequest(`/iam/external-partners/people/${partnerId}`, {})
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error deleting the partner.', showNotification),
      onSuccess: () =>
        showNotification({ message: 'Partner deactivated successfully', type: 'success' })
    }
  )
}

export const useDeleteManagerMutation = (showNotification: Function) => {
  return useMutation<void, Error, string>(
    async managerId => {
      await makeCoreDeleteRequest(`/iam/external-partners/managers/${managerId}`, {})
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error deleting the manager.', showNotification),
      onSuccess: () =>
        showNotification({ message: 'Manager deactivated successfully', type: 'success' })
    }
  )
}

export const useDeleteDelegateMutation = (showNotification: Function) => {
  return useMutation<void, Error, DeleteDelegateArgs>(
    async ({ managerId, delegateId }) => {
      await makeCoreDeleteRequest(
        `/iam/external-partners/managers/${managerId}/delegate/${delegateId}`,
        {}
      )
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error deleting the delegate.', showNotification),
      onSuccess: () =>
        showNotification({ message: 'Delegate deleted successfully', type: 'success' })
    }
  )
}

export const useDeleteAssignmentMutation = (showNotification: Function) => {
  return useMutation<void, Error, number>(
    async assignmentId => {
      await makeCoreDeleteRequest(`/iam/external-partners/assignments/${assignmentId}`, {})
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error deleting the assignment.', showNotification),
      onSuccess: () =>
        showNotification({ message: 'Assignment deleted successfully', type: 'success' })
    }
  )
}
